import React from 'react';
import { DollarSign, Calendar, Smartphone, Receipt, Clock, CheckCircle, CheckCircle2, ArrowRight, Copy, Building2, CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useTenant } from './TenantContext';
import { toast } from 'sonner';

const buildPaymentMethods = (s) => {
  const methods = [];
  const orange = (s?.orange_money_number || '').trim();
  const moov = (s?.moov_money_number || '').trim();
  const bankName = (s?.bank_name || '').trim();
  const bankAccount = (s?.bank_account || '').trim();
  const bankHolder = (s?.bank_holder || '').trim();

  if (orange) {
    methods.push({
      key: 'orange',
      label: 'Orange Money',
      number: orange,
      raw: orange.replace(/\s/g, ''),
      colors: 'from-orange-100 to-amber-50 border-orange-300',
      labelColor: 'text-orange-700',
      btnColor: 'border-orange-400 text-orange-700 hover:bg-orange-100',
      icon: Smartphone,
      iconBg: 'from-orange-500 to-yellow-500',
      hint: 'Transférez depuis votre compte Orange Money.',
    });
  }
  if (moov) {
    methods.push({
      key: 'moov',
      label: 'Moov Money',
      number: moov,
      raw: moov.replace(/\s/g, ''),
      colors: 'from-blue-100 to-sky-50 border-blue-300',
      labelColor: 'text-blue-700',
      btnColor: 'border-blue-400 text-blue-700 hover:bg-blue-100',
      icon: Smartphone,
      iconBg: 'from-blue-500 to-cyan-500',
      hint: 'Transférez depuis votre compte Moov Money.',
    });
  }
  if (bankName || bankAccount || bankHolder) {
    methods.push({
      key: 'bank',
      label: 'Virement bancaire',
      isBank: true,
      fields: [
        { label: 'Banque', value: bankName || 'À communiquer avec l\u2019agence' },
        { label: 'N° de compte / RIB', value: bankAccount || 'À communiquer avec l\u2019agence' },
        { label: 'Titulaire du compte', value: bankHolder || (s?.name || '') },
      ],
      colors: 'from-emerald-100 to-teal-50 border-emerald-300',
      labelColor: 'text-emerald-700',
      btnColor: 'border-emerald-400 text-emerald-700 hover:bg-emerald-100',
      icon: Building2,
      iconBg: 'from-emerald-500 to-teal-500',
      hint: 'Effectuez un virement bancaire depuis votre banque.',
    });
  }
  return methods;
};

const PaymentMethodCard = ({ method }) => {
  const copy = (value, name) => {
    navigator.clipboard?.writeText(value);
    toast.success(`${name} copié`);
  };
  const Icon = method.icon;
  return (
    <div className={`bg-gradient-to-r ${method.colors} border-2 rounded-lg p-4 shadow-sm`}
         data-testid={`payment-method-${method.key}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-9 h-9 bg-gradient-to-br ${method.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <p className={`text-xs font-semibold ${method.labelColor} uppercase tracking-wide`}>
            {method.label}
          </p>
        </div>
        {!method.isBank && (
          <Button
            size="sm" variant="outline"
            onClick={() => copy(method.raw, method.label)}
            className={method.btnColor}
            data-testid={`copy-${method.key}-btn`}
          >
            <Copy className="w-4 h-4 mr-1" /> Copier
          </Button>
        )}
      </div>
      {!method.isBank ? (
        <p className="text-2xl font-bold text-[#1B3A4B] font-mono tracking-wider mb-1" data-testid={`number-${method.key}`}>
          {method.number}
        </p>
      ) : (
        <div className="space-y-1.5 mb-1">
          {method.fields.map((f, idx) => (
            <div key={idx} className="flex items-center justify-between gap-2 bg-white/70 rounded px-3 py-1.5 border border-emerald-100"
                 data-testid={`bank-field-${idx}`}>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-wide text-gray-500">{f.label}</p>
                <p className="text-sm font-semibold text-[#1B3A4B] truncate">{f.value}</p>
              </div>
              <Button
                size="sm" variant="ghost"
                onClick={() => copy(f.value, f.label)}
                className="h-7 w-7 p-0 text-emerald-700 hover:bg-emerald-100"
                data-testid={`copy-bank-${idx}-btn`}
                title={`Copier ${f.label}`}
              >
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-gray-600">{method.hint}</p>
    </div>
  );
};

const TenantPayment = () => {
  const { profile, receipts, formatCurrency, formatDate, openPaymentDialog, agencySettings } = useTenant();
  const paymentMethods = buildPaymentMethods(agencySettings);
  return (
    <>
  {/* Mobile Money Payment Card (Jemenipay) */}
  <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-white">
    <CardHeader>
      <CardTitle className="text-lg flex items-center gap-2">
        <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-full flex items-center justify-center">
          <Smartphone className="w-5 h-5 text-white" />
        </div>
        Payer via Mobile Money
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="bg-white p-4 rounded-lg border border-orange-100">
        <div className="flex justify-between items-center mb-4">
          <div>
            <p className="text-sm text-gray-500">Loyer mensuel</p>
            <p className="text-2xl font-bold text-[#1B3A4B]">
              {formatCurrency(profile?.property?.monthly_rent || 0, profile?.property?.currency || 'XOF')}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Statut</p>
            {profile?.stats?.unpaid_amount > 0 ? (
              <Badge className="bg-red-100 text-red-800">
                {formatCurrency(profile?.stats?.unpaid_amount, profile?.property?.currency || 'XOF')} impaye
              </Badge>
            ) : (
              <Badge className="bg-green-100 text-green-800">A jour</Badge>
            )}
          </div>
        </div>
        
        <Button 
          onClick={openPaymentDialog}
          className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white py-6 text-lg"
          data-testid="pay-mobile-money-btn"
        >
          <Smartphone className="w-5 h-5 mr-2" />
          Payer mon loyer
        </Button>
        
        <p className="text-xs text-center text-gray-500 mt-3">
          Orange Money &middot; Moov Money &middot; Paiement securise Jemenipay
        </p>
      </div>

      {/* Numéros de paiement du bailleur (dynamiques par agence) */}
      {paymentMethods.length > 0 && (
        <div className="space-y-3" data-testid="payment-methods-block">
          <div className="text-xs font-semibold text-[#1B3A4B] uppercase tracking-wide flex items-center gap-1 pt-2">
            <CreditCard className="w-4 h-4 text-amber-600" />
            Autres options de paiement direct
          </div>
          {paymentMethods.map(m => (
            <PaymentMethodCard key={m.key} method={m} />
          ))}
        </div>
      )}

      {/* How it works */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-semibold mb-3">Comment ca marche ?</h4>
        <ol className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
            <span>Entrez votre numero Orange Money ou Moov Money</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
            <span>Recevez une notification push sur votre telephone</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
            <span>Validez directement depuis votre application mobile</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">OK</span>
            <span>Votre quittance est generee automatiquement</span>
          </li>
        </ol>
      </div>
    </CardContent>
  </Card>

  {/* Recent Payments */}
  <Card>
    <CardHeader>
      <CardTitle className="text-lg">Mes derniers paiements</CardTitle>
    </CardHeader>
    <CardContent>
      {receipts.length > 0 ? (
        <div className="space-y-3">
          {receipts.slice(0, 5).map((receipt) => (
            <div
              key={receipt.id}
              className="flex items-center justify-between p-3 rounded-lg border border-gray-100"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">{formatCurrency(receipt.amount, receipt.currency)}</p>
                  <p className="text-sm text-gray-500">{formatDate(receipt.payment_date)}</p>
                </div>
              </div>
              <Badge variant="outline">{receipt.payment_method === 'mobile_money' ? 'Orange Money' : receipt.payment_method}</Badge>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-gray-500 py-8">Aucun paiement enregistre</p>
      )}
    </CardContent>
  </Card>
    </>
  );
};

export default TenantPayment;
